import React, {
  useId,
  useMemo,
} from 'react';

import PropTypes from 'prop-types';
import {
  FormattedMessage,
  useIntl,
} from 'react-intl';

/** King-living Swiper wraps nav in `.swiper-navigation-pagination` — required for `INSTOCK_TILE_NAVIGATION` selectors. */
import ImageNotAvailable from '@amblique/king-living/app/components/image-not-available';
import {Swiper} from '@amblique/king-living/app/components/shared/swiper';
import {formatCurrencyHideTrailingZeroCents} from '@amblique/king-living/app/utils/format-currency-hide-zero-cents';
import {getDisplayImageSrc} from '@amblique/king-living/app/utils/product-display-image';
import {getMasterTileImages} from '@amblique/king-living/app/utils/product-images';
import {getPriceData} from '@amblique/king-living/app/utils/product-utils';
import msg from '@salesforce/retail-react-app/app/components/display-price/messages';
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image';
import Link from '@salesforce/retail-react-app/app/components/link';
import {
  AspectRatio,
  Badge,
  Box,
  Image,
  Text,
  useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui';
import {useCurrency} from '@salesforce/retail-react-app/app/hooks';
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation';

const TILE_IMAGE_WIDTHS = ['50vw', '50vw', '25vw', '25vw', '20vw'];

/**
 * Intrinsic px for the `width`/`height` HTML attributes on the tile image (KINDW-1833).
 * The image is absolutely filled inside an `AspectRatio` box, so the actual layout is
 * CSS-controlled; this 1:1 value only gives the browser explicit dimensions to satisfy the
 * "explicit width & height" requirement without affecting rendering.
 */
const TILE_IMAGE_INTRINSIC_PX = 1000;

/** Matches in-tile Swiper chrome (nav, progressbar, bullets). */
const INNER_TILE_SWIPER_CHROME =
  '.swiper-button-next, .swiper-button-prev, .swiper-pagination, .swiper-pagination-progressbar, .swiper-pagination-bullet';

const isInnerTileSwiperChrome = (target) => Boolean(target?.closest?.(INNER_TILE_SWIPER_CHROME));

const formatInstockTileCurrency = (value, currency, intl) => formatCurrencyHideTrailingZeroCents(intl, value, currency);

/**
 * Want It Sooner carousel tile — in-stock products with optional **In stock** pill.
 * Product grids (PLP, Contentful, cart upsell) use `RangeProductTile` instead.
 *
 * @param {object} props
 * @param {object} props.product - Full product object from getProducts API
 * @param {string} [props.to] - Link URL for the tile
 * @param {'default'|'wantItSooner'} [props.variant] — carousel uses `wantItSooner`
 * @param {boolean} [props.showHeroPromotionBadges] - Hero Sale/marketing pills; `false` on Want It Sooner
 * @param {boolean} [props.isShowInStockBadge] - **In stock** pill; Want It Sooner passes **`true`**
 */
const InstockProductTile = ({
  product,
  to,
  variant = 'default',
  isShowInStockBadge = false,
  showHeroPromotionBadges = true,
  onClick,
  ...rest
}) => {
  const productTitleId = useId();
  const styles = useMultiStyleConfig('InstockProductTile', {variant});
  const intl = useIntl();
  const {currency} = useCurrency();
  const navigate = useNavigation();

  const handleImageAreaClick = (e) => {
    if (!to) return;
    if (isInnerTileSwiperChrome(e.target)) {
      e.stopPropagation();

      return;
    }
    e.preventDefault();
    e.stopPropagation();
    onClick?.();
    navigate(to);
  };

  const images = useMemo(
    () => getMasterTileImages(product).filter(
      (img) => img.mediaType !== 'video' && (img.disBaseLink || img.link)
    ),
    [product]
  );

  const tileImages = useMemo(() => {
    if (images.length > 0) return images;

    const url = product?.image?.disBaseLink || product?.image?.link;

    return url ?
      [{disBaseLink: product?.image?.disBaseLink, link: product?.image?.link, alt: product?.image?.alt}] :
      [];
  }, [images, product?.image]);

  const title = product.name ?? product.productName ?? '';
  const shortDescription = product.shortDescription ?? '';
  const rawBadgeText = product.c_badgeText ?? product.badgeText ?? '';
  const trimmedMarketingBadge =
    typeof rawBadgeText === 'string' ? rawBadgeText.trim() : '';
  const badgeText = trimmedMarketingBadge;

  const currencyCode = product.currency ?? currency ?? 'AUD';
  const priceData = useMemo(() => getPriceData(product), [product]);

  const formattedTileCurrent = useMemo(() => {
    if (priceData?.currentPrice == null) return '';

    return formatInstockTileCurrency(priceData.currentPrice, currencyCode, intl);
  }, [intl, currencyCode, priceData?.currentPrice]);

  const formattedTileList = useMemo(() => {
    if (priceData?.listPrice == null) return '';

    return formatInstockTileCurrency(priceData.listPrice, currencyCode, intl);
  }, [intl, currencyCode, priceData?.listPrice]);

  const isOnSaleTile = Boolean(
    priceData?.currentPrice != null &&
    priceData?.listPrice != null &&
    priceData.listPrice > priceData.currentPrice
  );

  const cmsBadgeIsSaleOnly =
    trimmedMarketingBadge.replace(/\s+/g, ' ').toUpperCase().trim() === 'SALE';
  const showSalePill = isOnSaleTile || cmsBadgeIsSaleOnly;
  const marketingPillCopy =
    trimmedMarketingBadge && !cmsBadgeIsSaleOnly ? trimmedMarketingBadge : '';
  const showMarketingPill = Boolean(marketingPillCopy);
  const heroSaleVisible = Boolean(showHeroPromotionBadges && showSalePill);
  const heroMarketingVisible = Boolean(showHeroPromotionBadges && showMarketingPill);
  const showHeroBadgeCluster = Boolean(
    isShowInStockBadge || heroSaleVisible || heroMarketingVisible
  );

  let imageContent;

  if (tileImages.length === 0) {
    imageContent = (
      <AspectRatio {...styles.aspectRatio}>
        <ImageNotAvailable
          svgProps={styles.placeholderImage}
          wrapperProps={styles.placeholderWrapper}
        />
      </AspectRatio>
    );
  } else if (tileImages.length > 1) {
    imageContent = (
      <AspectRatio {...styles.aspectRatio}>
        <Box {...styles.swiperContainer}>
          <Swiper presets="instock_tile">
            {tileImages.map((img, i) => (
              <DynamicImage
                key={`img-${i}`}
                as={Image}
                src={getDisplayImageSrc(img)}
                widths={TILE_IMAGE_WIDTHS}
                imageProps={{
                  alt: img.alt || `${title} image ${i + 1}`,
                  htmlHeight: TILE_IMAGE_INTRINSIC_PX,
                  htmlWidth: TILE_IMAGE_INTRINSIC_PX,
                  loading: i === 0 ? 'eager' : 'lazy',
                  objectFit: 'contain',
                  width: '100%',
                  height: '100%',
                }}
              />
            ))}
          </Swiper>
        </Box>
      </AspectRatio>
    );
  } else {
    imageContent = (
      <AspectRatio {...styles.aspectRatio}>
        <DynamicImage
          as={Image}
          src={getDisplayImageSrc(tileImages[0])}
          widths={TILE_IMAGE_WIDTHS}
          imageProps={{
            alt: tileImages[0]?.alt || title,
            htmlHeight: TILE_IMAGE_INTRINSIC_PX,
            htmlWidth: TILE_IMAGE_INTRINSIC_PX,
            loading: 'eager',
            objectFit: 'contain',
            width: '100%',
            height: '100%',
          }}
        />
      </AspectRatio>
    );
  }

  const productInfoSection = (
    <Box data-testid="instock-product-tile-info" {...styles.infoSection}>
      {badgeText ?
        (
          <Text {...styles.badgeText} className="instock-product-tile__cms-badge">
            {badgeText}
          </Text>
        ) :
        null}
      <Box
        data-testid="instock-product-tile-title-desc"
        {...styles.titleDescriptionGroup}
      >
        <Text id={productTitleId} {...styles.title}>
          {title}
        </Text>
        {shortDescription && (
          <Text {...styles.description}>{shortDescription}</Text>
        )}
      </Box>

      {priceData?.currentPrice != null && formattedTileCurrent !== '' && (
        <Box {...styles.priceSection}>
          <Text
            as="b"
            {...(isOnSaleTile ? styles.priceValueSale : styles.priceValue)}
            aria-label={intl.formatMessage(msg.ariaLabelCurrentPrice, {
              currentPrice: formattedTileCurrent,
            })}
          >
            {isOnSaleTile ? (
              <FormattedMessage
                defaultMessage="Sale price from"
                id="range_product_tile.price.sale_price_from"
              />
            ) : (
              <FormattedMessage
                defaultMessage="Full price from"
                id="range_product_tile.price.full_price_from"
              />
            )}{' '}
            {formattedTileCurrent}
          </Text>
          {isOnSaleTile && formattedTileList ?
            (
              <Box {...styles.priceDiscountRow}>
                <Text as="span" {...styles.priceOriginalLabel}>
                  <FormattedMessage
                    defaultMessage="Full price from"
                    id="range_product_tile.price.full_price_from"
                  />
                </Text>
                {' '}
                <Text
                  as="span"
                  {...styles.priceStrike}
                  aria-label={intl.formatMessage(msg.ariaLabelListPrice, {
                    listPrice: formattedTileList,
                  })}
                >
                  {formattedTileList}
                </Text>
              </Box>
            ) :
            null}
        </Box>
      )}
    </Box>
  );

  const tileContent = (
    <Box
      data-kl-instock-variant={variant}
      data-testid="instock-product-tile"
      {...styles.container}
      {...rest}
    >
      <Box
        data-testid="instock-product-tile-image"
        {...styles.imageWrapper}
        className="swiper-instock-tile"
        cursor={to ? 'pointer' : undefined}
        onClick={to ? handleImageAreaClick : undefined}
      >
        {imageContent}
        {showHeroBadgeCluster &&
          (
            <Box data-testid="instock-product-tile-badges" {...styles.heroBadgeStack}>
              {isShowInStockBadge && (
                <Badge
                  {...styles.instockBadge}
                  className="kl-in-stock-badge kl-product-tile-badge"
                  data-testid="instock-product-tile-in-stock-badge"
                >
                  <FormattedMessage
                    defaultMessage="In stock"
                    id="instock_product_tile.badge.in_stock"
                  />
                </Badge>
              )}
              {heroSaleVisible && (
                <Badge
                  {...styles.infoSaleBadge}
                  className="kl-product-tile-badge"
                  data-testid="instock-product-tile-sale-badge"
                >
                  <FormattedMessage
                    defaultMessage="Sale"
                    id="instock_product_tile.badge.sale"
                  />
                </Badge>
              )}
              {heroMarketingVisible && (
                <Badge
                  {...styles.infoMarketingBadge}
                  className="kl-product-tile-badge"
                  data-testid="instock-product-tile-marketing-badge"
                >
                  {marketingPillCopy}
                </Badge>
              )}
            </Box>
          )}
      </Box>

      {to ? (
        <Link
          aria-labelledby={title ? productTitleId : undefined}
          data-testid="instock-product-tile-link"
          to={to}
          aria-label={
            title ?
              undefined :
              intl.formatMessage({
                defaultMessage: 'View product details',
                id: 'instock_product_tile.link.view_generic',
              })
          }
          {...styles.link}
          onClick={() => onClick?.()}
        >
          {productInfoSection}
        </Link>
      ) : (
        productInfoSection
      )}
    </Box>
  );

  return tileContent;
};

InstockProductTile.propTypes = {
  product: PropTypes.object.isRequired,
  to: PropTypes.string,
  onClick: PropTypes.func,
  variant: PropTypes.oneOf(['default', 'wantItSooner', 'upsell', 'recommendation']),
  isShowInStockBadge: PropTypes.bool,
  showHeroPromotionBadges: PropTypes.bool,
};

const MemoInstockProductTile = React.memo(InstockProductTile);

MemoInstockProductTile.displayName = 'InstockProductTile';

export default MemoInstockProductTile;
